import { computed, ref, watch } from 'vue'
import { useLatestUpdates } from '@/composables/useLatestUpdates'
import {
  SETTING_KEYS,
  useUserPreferences,
} from '@/composables/useUserPreferences'
import { followupsCaseUrl, DL_REF } from '@/utils/deepLinks'

/**
 * Browser-level alerts when new follow-ups arrive.
 *
 *   - Watches `useLatestUpdates.items` for IDs that weren't in the
 *     previous poll. Each new ID triggers a single Notification API
 *     toast + a short Web Audio "beep" (no audio file needed).
 *   - **Two gates** must be open for a follow-up to trigger an alert:
 *       1. The browser-local master toggle in `localStorage`
 *          (controls the device — Notification permission + sound).
 *       2. The per-company server-side preference
 *          `notifications.tracking.{companyId}.webNotificationsEnabled`,
 *          set from the user settings panel. Defaults to `false`, so
 *          a brand-new user gets no surprises until they opt in.
 *   - First-load priming: we record the current set of unread IDs
 *     without notifying, so the user doesn't get a notification storm
 *     when the page initially loads.
 *
 * The composable itself is a singleton via module-level refs so the
 * "previously seen" set survives navigation between pages while the
 * tab stays open.
 */

const STORAGE_KEY = 'followUpNotifications.enabled'

const _enabled = ref(false)
const _permission = ref('default')
const _previousIds = new Set()
const _primed = ref(false)
let _watcherStarted = false

function loadPreference() {
  if (typeof window === 'undefined') return
  try {
    _enabled.value = window.localStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    _enabled.value = false
  }
}

function savePreference(value) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? 'true' : 'false')
  } catch {
    // Private mode / storage disabled — non-fatal.
  }
}

function refreshPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    _permission.value = 'unsupported'

    return
  }
  _permission.value = Notification.permission
}

async function requestPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return false
  }
  try {
    // The Notification.requestPermission API exists in two shapes:
    //   - returns a Promise (modern browsers)
    //   - takes a callback (legacy Safari)
    // We support both — the wrapper resolves either way.
    const result = await new Promise(resolve => {
      const ret = Notification.requestPermission(resolve)
      if (ret && typeof ret.then === 'function') {
        ret.then(resolve, () => resolve(Notification.permission))
      }
    })

    _permission.value = result || Notification.permission

    return _permission.value === 'granted'
  } catch (err) {
    console.error('[useFollowUpNotifications] requestPermission failed', err)
    _permission.value = Notification.permission

    return false
  }
}

/**
 * Two-tone beep generated with the Web Audio API. No external audio
 * file required, plays inline, and stays under ~250ms so it doesn't
 * feel intrusive. May be silently blocked until the user has
 * interacted with the page (browser autoplay policy) — that's fine,
 * the user can click anywhere to "prime" audio for the session.
 */
function playBeep() {
  if (typeof window === 'undefined') return
  const Ctor = window.AudioContext || window.webkitAudioContext
  if (!Ctor) return
  try {
    const ctx = new Ctor()
    const now = ctx.currentTime
    const tones = [880, 1320]

    tones.forEach((freq, i) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()

      osc.connect(gain).connect(ctx.destination)
      osc.type = 'sine'
      osc.frequency.value = freq

      const start = now + i * 0.12

      gain.gain.setValueAtTime(0.001, start)
      gain.gain.exponentialRampToValueAtTime(0.14, start + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.2)
      osc.start(start)
      osc.stop(start + 0.22)
    })

    // Close the context shortly after the beep so it doesn't pile up
    // in long-lived sessions.
    setTimeout(() => ctx.close().catch(() => {}), 400)
  } catch {
    // Audio context creation can fail in headless / restricted
    // environments — ignore.
  }
}

function showNotification(item) {
  if (typeof window === 'undefined' || !('Notification' in window)) return
  if (_permission.value !== 'granted') return

  // Title: CompanyName - LeadAgency - Author - CaseTitle - ChildName.
  // Any missing piece is dropped so we never render stray separators.
  const company = item.companyName || item.companyShortName
  const leadAgency = item.leadAgencyShortName || item.leadAgencyName
  const author = item.authorShortName || item.authorName

  const title =
    [company, leadAgency, author, item.caseTitle, item.childName]
      .filter(Boolean)
      .join(' - ') || 'New follow-up'

  // Body: a short, plain-text fragment of the note (HTML stripped,
  // whitespace collapsed, truncated with an ellipsis).
  const MAX_BODY = 140
  let body = (item.notes || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (body.length > MAX_BODY) body = `${body.slice(0, MAX_BODY).trimEnd()}…`
  if (!body) body = `${author || 'Someone'} left a new note.`

  try {
    const notification = new Notification(title, {
      body,
      icon: '/favicon.ico',
      tag: `followup-${item.caseId}`, // dedupe per case
      renotify: true,
    })

    notification.onclick = () => {
      window.focus()

      // Navigate via location since we don't have the router from a module-level
      // handler. The deep-link contract builds the URL (and uses the correct
      // `fuid` param — this used to send `fid`, which the page silently ignored,
      // so the follow-up never scrolled into view).
      window.location.href = followupsCaseUrl({
        companyId: item.companyId,
        agencyId: item.leadAgencyId,
        caseId: item.caseId,
        followUpId: item.followUpId,
        ref: DL_REF.webNotification,
      })
      notification.close()
    }
  } catch {
    // Some browsers throw if the page hasn't been interacted with —
    // ignore; the sound still fires.
  }
}

/**
 * Check whether the user has web notifications enabled for the
 * follow-up's company in their server-side preferences. Defaults to
 * `false` when the prefs map hasn't loaded yet or no row exists for
 * that company — fail-safe: better to under-notify than spam a user
 * who never opted in.
 */
function isCompanyEnabled(prefs, companyId) {
  if (!companyId) return false

  const setting = prefs.get(
    SETTING_KEYS.notificationsTracking(companyId),
  )

  return !!setting?.webNotificationsEnabled
}

function startWatcher() {
  if (_watcherStarted) return
  _watcherStarted = true

  const latest = useLatestUpdates()
  const prefs = useUserPreferences()

  // Watch the items collection — pulled in by polling, visibility
  // refresh, or explicit invalidation. We diff by `followUpId` so
  // the same case getting a *different* unread follow-up still
  // counts as new.
  watch(
    () => latest.items.value,
    items => {
      const currentIds = new Set(items.map(i => String(i.followUpId)))

      // First time we see a populated list: just record, don't
      // notify. Otherwise every page reload would fire a storm.
      if (!_primed.value) {
        for (const id of currentIds) _previousIds.add(id)
        _primed.value = true

        return
      }

      // Identify arrivals before we mutate `_previousIds`, then sync
      // the set with the current snapshot — handles both arrivals
      // and dismissals.
      const newOnes = items.filter(
        i => !_previousIds.has(String(i.followUpId)),
      )

      _previousIds.clear()
      for (const id of currentIds) _previousIds.add(id)

      if (newOnes.length === 0) return

      // If the device-level master is off, swallow the batch — we
      // already updated `_previousIds`, so the user won't get a
      // backlog notification storm when they re-enable.
      if (!_enabled.value) return

      // Filter to follow-ups whose company has web notifications
      // enabled in the user's server-side settings. The toggle in
      // the user settings panel is the source of truth here.
      const allowed = newOnes.filter(i =>
        isCompanyEnabled(prefs, i.companyId),
      )

      if (allowed.length === 0) return

      // One sound per batch (avoids a chord of beeps if N arrived at
      // once); one notification per follow-up (deduped by case via
      // the `tag` so the same case won't stack).
      playBeep()
      for (const item of allowed) {
        showNotification(item)
      }
    },
    { deep: false },
  )
}

/**
 * Reset for logout — clears the "previously seen" set so the next
 * session starts from a clean slate and won't fire alerts for the
 * unread follow-ups that were already on the previous user's view.
 * The user's `_enabled` preference + browser permission persist
 * because they're per-device, not per-session.
 */
export function resetFollowUpNotificationsState() {
  _previousIds.clear()
  _primed.value = false
}

export function useFollowUpNotifications() {
  // Always refresh on the client so the first hydration call picks
  // up the real `Notification.permission` value. The previous "only
  // when default" guard left the permission stuck at `unsupported`
  // after an SSR pass (where `window` is undefined).
  refreshPermission()
  if (typeof window !== 'undefined' && !_primed.value) loadPreference()
  startWatcher()

  async function enable() {
    if (_permission.value === 'unsupported') return false
    if (_permission.value !== 'granted') {
      const ok = await requestPermission()
      if (!ok) return false
    }
    _enabled.value = true
    savePreference(true)

    return true
  }

  function disable() {
    _enabled.value = false
    savePreference(false)
  }

  async function toggle() {
    return _enabled.value ? (disable(), false) : await enable()
  }

  const isSupported = computed(() => _permission.value !== 'unsupported')
  const isBlocked = computed(() => _permission.value === 'denied')

  return {
    enabled: _enabled,
    permission: _permission,
    isSupported,
    isBlocked,
    enable,
    disable,
    toggle,
    refreshPermission,
  }
}
